'use client';

import { useState, type ReactNode } from 'react';
import { OrgAccountSwitcher } from '@/components/layout/org-account-switcher';
import { SheetSlideSection } from '@/components/layout/sheet-slide-section';
import { UserAccountMenu } from '@/components/layout/user-account-menu';
import { cn } from '@/lib/utils';

type NavbarAccountControlsProps = {
  compact?: boolean;
  variant?: 'dropdown' | 'sheet';
  theme?: 'navbar' | 'enterprise';
  /** Mobile sheet header: skip inner border; pair with `headerTrailing` (e.g. close). */
  embeddedInHeader?: boolean;
  headerTrailing?: ReactNode;
  compactSheetHeader?: boolean;
  /** Close mobile sheet when avatar navigates to profile. */
  onProfileNavigate?: () => void;
};

type SheetSection = 'org';

export function NavbarAccountControls({
  compact = false,
  variant = 'dropdown',
  theme = 'navbar',
  embeddedInHeader = false,
  headerTrailing,
  compactSheetHeader = false,
  onProfileNavigate,
}: NavbarAccountControlsProps) {
  const [openSection, setOpenSection] = useState<SheetSection | null>(null);

  function toggleSection(section: SheetSection) {
    setOpenSection((current) => (current === section ? null : section));
  }

  if (variant === 'sheet') {
    const isClosed = openSection === null;
    const isEnterpriseTheme = theme === 'enterprise';
    const isCompact = compactSheetHeader || embeddedInHeader;

    return (
      <div className="flex w-full flex-col">
        <div
          className={cn(
            'flex min-w-0 items-center',
            isCompact ? 'gap-1' : 'gap-2',
            embeddedInHeader ? 'py-0' : 'pb-2',
            !embeddedInHeader &&
              (isEnterpriseTheme ? 'px-0' : '-mx-1.5 px-1.5'),
            !embeddedInHeader &&
              isClosed &&
              (isEnterpriseTheme
                ? 'border-b border-emerald-800/70'
                : 'border-b border-gray-200 dark:border-gray-800'),
          )}
        >
          <OrgAccountSwitcher
            variant="sheet"
            sheetPart="trigger"
            sheetExpanded={openSection === 'org'}
            onSheetToggle={() => toggleSection('org')}
            triggerClassName={cn(isCompact && 'min-w-0 flex-1')}
            theme={theme}
            compactSheetHeader={isCompact}
          />
          {!embeddedInHeader ? (
            <UserAccountMenu
              variant="sheet"
              sheetPart="trigger"
              theme={theme}
              compactSheetHeader={isCompact}
              onSheetProfileNavigate={onProfileNavigate}
            />
          ) : null}
          {headerTrailing}
        </div>

        <SheetSlideSection
          open={openSection === 'org'}
          bleed={!isEnterpriseTheme}
          panelClassName={
            isEnterpriseTheme ? 'bg-emerald-950/70 shadow-inner shadow-emerald-950/40' : undefined
          }
        >
          <OrgAccountSwitcher variant="sheet" sheetPart="panel" theme={theme} />
        </SheetSlideSection>
      </div>
    );
  }

  return (
    <>
      <OrgAccountSwitcher compact={compact} />
      <span
        aria-hidden="true"
        className="hidden h-6 w-px shrink-0 bg-gray-300 dark:bg-gray-600 md:inline-block"
      />
      <UserAccountMenu />
    </>
  );
}
